const EXTENSIONS_NAMESPACE = 'http://citrix.com/authentication/response/extensions/1';
const WEBVIEW_NAMESPACE = 'http://citrix.com/authentication/response/webview/1';

export type ParsedRequirementNodes = ParsedRequirementNode[];
export interface ParsedRequirementNode {
	credential: CredentialNode;
	label: LabelNode;
	input: InputNode;
}
export interface CredentialNode {
	id?: string;
	type: string;
	saveID?: string;
	value?: string;
	webViewData?: WebViewNode;
}

export interface WebViewNode {
	startUrl?: string;
}

export interface LabelNode {
	type: string;
	src?: string;
	text?: string;
	highlightFields?: string[];
}

export interface InputNode {
	assistiveText: string;
	text: TextNode;
	radioButton: SelectNode;
	comboBox: SelectNode;
	multiComboBox: SelectNode;
	button: string;
	checkBox: CheckBoxNode;
	certificateChoices: CertificatesNode;
}

export interface TextNode {
	secret: boolean;
	readOnly: boolean;
	flatReadOnly: boolean;
	initialValue: string;
	mustNotBeEmpty: boolean;
}

export interface CheckBoxNode {
	initialValue: boolean;
}

export interface SelectNode {
	initialSelection?: string;
	displayValues: DisplayValueNode[];
}

export interface DisplayValueNode {
	display: string;
	value: string;
	selected?: boolean;
}

export interface CertificatesNode {
	initialSelection: string;
	viewCertificateLinkText: string;
	certificates: CertificateNode[];
}

export interface CertificateNode {
	value: string;
	display: string;
	properties: string[];
}

export enum AuthUILabelType {
	None = 'none',
	Image = 'image',
	Plain = 'plain',
	Heading = 'heading',
	Heading2 = 'heading2',
	Information = 'information',
	Warning = 'warning',
	Error = 'error',
	Confirmation = 'confirmation',
	Inprogress = 'inprogress',
	Nsg120n = 'nsg-l20n',
	Nsgloginheading = 'nsg-login-heading',
	Hint = 'hint',
}

export class RequirementNodeParsingError extends Error {
	constructor(errorMsg: string) {
		super();
		this.message = errorMsg;
	}
}

export const RequirementNodeParser = {
	parseRequirementNodes(requirementsXml: string): ParsedRequirementNode[] {
		const document = new DOMParser().parseFromString(requirementsXml, 'text/xml');
		const requirementNodes = document.getElementsByTagName('Requirement');
		const parsedNodes: ParsedRequirementNode[] = [];
		for (const node of requirementNodes) {
			const parsedNode = RequirementNodeParser.parseRequirementNode(node, true);
			parsedNodes.push(parsedNode);
		}
		return parsedNodes;
	},

	parseRequirementNode(
		node: Element,
		allowExtendedInputs: boolean
	): ParsedRequirementNode {
		const credentialNode = getElementByTagName('Credential', node);
		const labelNode = getElementByTagName('Label', node);
		const inputNode = getElementByTagName('Input', node, true);

		return {
			credential: parseCredentialNode(credentialNode),
			label: parseLabelNode(labelNode),
			input: parseInputNode(inputNode, allowExtendedInputs),
		};
	},
};

function parseCredentialNode(credentialNode: Element): CredentialNode {
	const idNode = getElementByTagName('ID', credentialNode, true); // Ensure ID is valid
	let id = null;
	if (idNode) {
		id = idNode.textContent;
	}
	const typeNode = getElementByTagName('Type', credentialNode);
	const parsedCredentialNode: CredentialNode = {
		id,
		type: typeNode.textContent,
	};

	const saveIdNode = getElementByTagName('SaveID', credentialNode, true);
	if (saveIdNode) {
		parsedCredentialNode.saveID = saveIdNode.textContent;
	}

	const valueNode = parseExtNameSpaceData(credentialNode, 'Value');
	if (valueNode) {
		parsedCredentialNode.value = valueNode.textContent;
	}

	const webViewNode = parseWebViewCredentialNameSpaceData(credentialNode, 'WebView');
	if (webViewNode) {
		parsedCredentialNode.webViewData = webViewNode;
	}
	return parsedCredentialNode;
}

function parseExtNameSpaceData(credentialNode: Element, tagName: string): Element {
	const valueNodes = credentialNode.getElementsByTagNameNS(EXTENSIONS_NAMESPACE, tagName);
	if (!valueNodes || valueNodes.length !== 1) {
		return null;
	}
	return valueNodes[0];
}

function parseWebViewCredentialNameSpaceData(
	credentialNode: Element,
	tagName: string
): WebViewNode {
	const webviewNodes = credentialNode.getElementsByTagNameNS(WEBVIEW_NAMESPACE, tagName);
	if (!webviewNodes || webviewNodes.length !== 1) {
		return null;
	}

	const webViewNode = webviewNodes[0];
	try {
		// Parse the Start URL (required)
		const startUrlElements = webViewNode.getElementsByTagNameNS(
			WEBVIEW_NAMESPACE,
			'StartUrl'
		);
		if (!startUrlElements || startUrlElements.length !== 1) {
			return {
				startUrl: null,
			};
		}
		return {
			startUrl: startUrlElements[0].textContent,
		};
	} catch (e) {
		throw new RequirementNodeParsingError(
			'could not parse webview data item: ' + webViewNode
		);
	}
}

function parseLabelNode(labelNode: Element): LabelNode {
	const type = getElementByTagName('Type', labelNode);
	if (!type) {
		return null;
	}
	const typeText = type.textContent;
	if (typeText === AuthUILabelType.None || typeText === '') {
		return {
			type: AuthUILabelType.None,
		};
	} else if (typeText === AuthUILabelType.Image) {
		return {
			type: typeText,
			src: getElementByTagName('Binary', labelNode).textContent,
			highlightFields: parseHighlightFields(labelNode),
		};
	} else if (Object.values<string>(AuthUILabelType).includes(typeText)) {
		return {
			type: typeText,
			text: getElementByTagName('Text', labelNode)?.textContent,
			highlightFields: parseHighlightFields(labelNode),
		};
	}

	throw new RequirementNodeParsingError('Unrecognised label type :' + typeText);
}

function parseHighlightFields(labelNode: Element): string[] {
	const highlightFields: string[] = [];
	const fieldsToHighlightNodes = getElementsByTagName('HighlightFields', labelNode);
	for (const fields of fieldsToHighlightNodes) {
		const credentialIdNodes = getElementsByTagName('CredentialID', fields);
		for (const node of credentialIdNodes) {
			highlightFields.push(node.textContent);
		}
	}
	return highlightFields;
}

function parseInputNode(inputNode: Element, allowExtendedInputs: boolean): InputNode {
	if (!inputNode) {
		return null;
	}
	const assistiveTextNode = getElementByTagName('AssistiveText', inputNode, true);
	const { textContent: assistiveText = null } = assistiveTextNode || {};
	const text = parseTextNode(getElementByTagName('Text', inputNode, true));
	const radioButton = parseRadioButton(
		getElementByTagName('RadioButton', inputNode, true)
	);
	const comboBox = parseComboBox(getElementByTagName('ComboBox', inputNode, true));
	const multiComboBox = parseMultiComboBox(
		getElementByTagName('MultiComboBox', inputNode, true)
	);
	const button = parseButton(getElementByTagName('Button', inputNode, true));
	const checkBox = parseCheckBox(getElementByTagName('CheckBox', inputNode, true));
	let certificateChoices = null;
	if (allowExtendedInputs) {
		certificateChoices = parseCertificates(
			getElementByTagName('CertificateChoice', inputNode, true)
		);
	}

	return {
		assistiveText,
		text,
		radioButton,
		comboBox,
		multiComboBox,
		button,
		checkBox,
		certificateChoices,
	};
}

function parseTextNode(textNode: Element): TextNode {
	if (!textNode) {
		return null;
	}
	const secretNodeText = getElementByTagName('Secret', textNode, true)?.textContent;
	const secret = secretNodeText ? secretNodeText.toLowerCase() === 'true' : false;

	const readOnlyNodeText = getElementByTagName('ReadOnly', textNode, true)?.textContent;
	const readOnly = readOnlyNodeText ? readOnlyNodeText.toLowerCase() === 'true' : false;

	const flatReadOnlyNodeText = getElementByTagName(
		'FlatStyle',
		textNode,
		true
	)?.textContent;
	const flatReadOnly = flatReadOnlyNodeText
		? readOnly && flatReadOnlyNodeText?.toLowerCase() == 'true'
		: false;

	const { textContent: initialValue = '' } =
		getElementByTagName('InitialValue', textNode, true) || {};

	const constraint = getElementByTagName('Constraint', textNode, true)?.textContent;
	const mustNotBeEmpty = constraint === '.+';

	return {
		secret,
		readOnly,
		flatReadOnly,
		initialValue,
		mustNotBeEmpty,
	};
}

function parseRadioButton(radioButtonNode: Element): SelectNode {
	return parseComboBox(radioButtonNode);
}

function parseComboBox(comboBoxNode: Element): SelectNode {
	if (!comboBoxNode) {
		return null;
	}
	const initialSelectionNode = getElementByTagName(
		'InitialSelection',
		comboBoxNode,
		true
	);
	const { textContent: initialSelection = null } = initialSelectionNode || {};
	const displayValues = getDisplayValues(comboBoxNode, false);

	return {
		initialSelection: initialSelection,
		displayValues,
	};
}

function parseMultiComboBox(multiComboBoxNode: Element): SelectNode {
	if (!multiComboBoxNode) {
		return null;
	}
	const displayValues = getDisplayValues(multiComboBoxNode, true);
	return {
		displayValues,
	};
}

function getDisplayValues(
	comboBoxNode: Element,
	parseSelectNodes: boolean
): DisplayValueNode[] {
	const displayValuesNode = getElementByTagName('DisplayValues', comboBoxNode);

	const displayValues: DisplayValueNode[] = [];
	const displayValueNodes = getElementsByTagName('DisplayValue', displayValuesNode);
	for (const node of displayValueNodes) {
		let selectNode;
		const displayValueNode = node;
		const displayNode = getElementByTagName('Display', displayValueNode);

		const valueNode = getElementByTagName('Value', displayValueNode);

		if (parseSelectNodes) {
			selectNode = getElementByTagName('Select', displayValueNode, true);
		}

		let displayValue: DisplayValueNode = null;
		if (parseSelectNodes) {
			displayValue = {
				display: displayNode.textContent,
				value: valueNode.textContent,
				selected: selectNode.textContent.toLowerCase() === 'true',
			};
		} else {
			displayValue = {
				display: displayNode.textContent,
				value: valueNode.textContent,
			};
		}
		displayValues.push(displayValue);
	}
	return displayValues;
}

function parseButton(buttonNode: Element): string {
	if (!buttonNode) {
		return null;
	}
	return buttonNode.textContent;
}

function parseCheckBox(checkBoxNode: Element): CheckBoxNode {
	if (!checkBoxNode) {
		return null;
	}
	const initialValueText = getElementByTagName(
		'InitialValue',
		checkBoxNode,
		true
	)?.textContent;
	const initialValue = initialValueText
		? initialValueText.toLowerCase() === 'true'
		: false;
	return { initialValue };
}

function parseCertificates(certificateChoiceNode: Element): CertificatesNode {
	if (!certificateChoiceNode) return null;
	const certNodes = getElementsByTagName('Certificate', certificateChoiceNode);
	const parsedCerts = parseCertificateNodes(certNodes);
	return {
		initialSelection:
			getElementByTagName('InitialSelection', certificateChoiceNode, true)?.textContent ??
			null,
		viewCertificateLinkText: getElementByTagName(
			'ViewCertificateLinkText',
			certificateChoiceNode
		).textContent,
		certificates: parsedCerts,
	};
}

function parseCertificateNodes(certNodes: HTMLCollectionOf<Element>): CertificateNode[] {
	const parsedCerts: CertificateNode[] = [];
	for (const certificateNode of certNodes) {
		const properties: string[] = [];
		const propertyNodes = getElementsByTagName('Property', certificateNode);
		for (const node of propertyNodes) {
			properties.push(node.textContent);
		}
		const cert = {
			value: getElementByTagName('Value', certificateNode).textContent,
			display: getElementByTagName('Display', certificateNode).textContent,
			properties: properties,
		};
		parsedCerts.push(cert);
	}
	return parsedCerts;
}

/**
 *Function to get atmost single element from node based on tagname.
 *If isNoElementAllowed set to true then number of elements expected in return can be 0 or 1 otherwise it will throw error.
 *If isNoElementAllowed set to false(Default value) then number of elements expected in return can be 1 only otherwise it will throw error.
 * @param {string} tag Tag name for the element to find in node
 * @param {Element} node Node in which element to search
 * @param {boolean} [isNoElementAllowed=false] flag to inform method that no element is possible with this node, default is set to false.
 * @return {Element} returns single element if there is only one element found in node, otherwise return null.
 */
function getElementByTagName(
	tag: string,
	node: Element,
	isNoElementAllowed = false
): Element {
	const elements = getElementsByTagName(tag, node);
	const isValidElement = isNoElementAllowed
		? verifyAtmostOneElement(tag, node.nodeName, elements.length)
		: verifyOnlyOneElement(tag, node.nodeName, elements.length);
	if (isValidElement && elements.length === 1) {
		return elements[0];
	}
	return null;
}

/**
 *Verify that node contains either '0' or '1' element with the tag name.
 *
 * @param {string} tag Tag name for the element to find in node
 * @param {string} nodeName Node name in which we are looking for element.
 * @param {number} elementsCount Number of elements with tagname found in node.
 * @return {boolean} return true if there is no error, otherwise log error and return false.
 */
function verifyAtmostOneElement(
	tag: string,
	nodeName: string,
	elementsCount: number
): boolean {
	if (elementsCount > 1) {
		throw new RequirementNodeParsingError(
			`Must have atmost one ${tag} node in a ${nodeName} node`
		);
	}
	return true;
}

/**
 *Verify that node contains only '1' element with the tag name.
 *
 * @param {string} tag Tag name for the element to find in node
 * @param {string} nodeName Node name in which we are looking for element.
 * @param {number} elementsCount Number of elements with tagname found in node.
 * @return {boolean} return true if there is no error, otherwise log error and return false.
 */
function verifyOnlyOneElement(
	tag: string,
	nodeName: string,
	elementsCount: number
): boolean {
	if (elementsCount != 1) {
		throw new RequirementNodeParsingError(
			`Must have only one ${tag} node in a ${nodeName} node`
		);
	}
	return true;
}
/**
 *Get all elements with the tagname in the node.
 *
 * @param {string} tag Tag name for the element to find in node
 * @param {Element} node Node in which element to search
 * @return {HTMLCollectionOf<Element>} returns collection of element matches the tagname in the node
 */
function getElementsByTagName(tag: string, node: Element): HTMLCollectionOf<Element> {
	return node.getElementsByTagName(tag);
}
